import { UploadSimple } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { fileMatchesAskV2Accept } from "./acceptedFiles";
import {
  ChatFileDropTargetContext,
  type ChatFileDropAddFiles,
} from "./chatFileDropTarget";

type OverlayBand = {
  left: number;
  width: number;
  top: number;
  height: number;
};

/**
 * Bottom third of the chat column, in viewport coordinates.
 * why: a fixed band stays on screen — absolute inset-0 tracks the thread and shifts the composer.
 */
export function chatDropOverlayBand(rect: {
  left: number;
  width: number;
  bottom: number;
  height: number;
}): OverlayBand {
  const height = rect.height / 3;
  return {
    left: rect.left,
    width: rect.width,
    top: rect.bottom - height,
    height,
  };
}

const isExternalFileDrag = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes("Files");

type AskV2ChatFileDropProps = {
  /** Read-only chats ignore drops and show no overlay. */
  disabled?: boolean;
  /** Direct sink. Used instead of the composer registered through context. */
  onFilesDropped?: (files: File[]) => void;
  children: ReactNode;
};

export function AskV2ChatFileDrop({
  disabled = false,
  onFilesDropped,
  children,
}: AskV2ChatFileDropProps) {
  const { t } = useTranslation("ui");
  const zoneRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<ChatFileDropAddFiles | null>(null);
  const dragDepth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [band, setBand] = useState<OverlayBand | null>(null);

  const measure = useCallback(() => {
    const zone = zoneRef.current;
    if (!zone) return null;
    return chatDropOverlayBand(zone.getBoundingClientRect());
  }, []);

  const clearDrag = useCallback(() => {
    dragDepth.current = 0;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const sync = () => setBand(measure());
    // why: an OS file drag cancelled outside this document never emits dragend here
    const endAbandonedDrag = () => clearDrag();
    const endIfLeftViewport = (event: globalThis.DragEvent) => {
      if (event.relatedTarget != null) return;
      clearDrag();
    };
    window.addEventListener("resize", sync);
    window.addEventListener("blur", endAbandonedDrag);
    document.addEventListener("drop", endAbandonedDrag);
    document.addEventListener("dragleave", endIfLeftViewport);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("blur", endAbandonedDrag);
      document.removeEventListener("drop", endAbandonedDrag);
      document.removeEventListener("dragleave", endIfLeftViewport);
    };
  }, [isDragging, measure, clearDrag]);

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      if (disabled || !isExternalFileDrag(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      if (dragDepth.current === 1) {
        setBand(measure());
        setIsDragging(true);
      }
    },
    [disabled, measure],
  );

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (disabled || !isExternalFileDrag(event)) return;
      event.preventDefault();
    },
    [disabled],
  );

  // why: never guarded by disabled — a refetch can flip it mid-drag and a guard here strands the overlay
  const handleDragLeave = useCallback((event: DragEvent) => {
    if (!isExternalFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (disabled || !isExternalFileDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      clearDrag();
      const files = Array.from(event.dataTransfer?.files ?? []).filter(
        fileMatchesAskV2Accept,
      );
      if (files.length === 0) return;
      // why: exclusive with the context sink — invoking both would attach the same file twice
      (onFilesDropped ?? targetRef.current)?.(files);
    },
    [clearDrag, disabled, onFilesDropped],
  );

  return (
    <ChatFileDropTargetContext.Provider value={targetRef}>
      <div
        ref={zoneRef}
        className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col"
        data-testid="ask-v2-chat-file-drop"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {children}
        {isDragging && band ? (
          <div
            className="pointer-events-none fixed z-50 flex flex-col justify-end bg-gradient-to-t from-puntt-accent-5/80 via-puntt-accent-3/50 to-transparent text-puntt-accent-11 backdrop-blur-sm"
            style={{
              left: band.left,
              width: band.width,
              top: band.top,
              height: band.height,
              maskImage: "linear-gradient(to top, black 50%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to top, black 50%, transparent 100%)",
            }}
            data-testid="ask-v2-chat-drop-overlay"
            role="status"
            aria-label={t("dropZone.dropFilesToAttach")}
          >
            <div className="m-6 flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-puntt-accent-9 px-8 py-6">
              <UploadSimple size={32} aria-hidden="true" />
              <Text size="5" weight="medium">
                {t("dropZone.dropFilesToAttach")}
              </Text>
            </div>
          </div>
        ) : null}
      </div>
    </ChatFileDropTargetContext.Provider>
  );
}
